import React, {useContext, useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next";
import {AppContext} from "../authProvider/AuthProvider";
import {UiPopup} from "../ui-components/popup/UiPopup";
import {GameResult} from "./gameResult";
import {getGameComponent} from "../../models/Game/GameComponent/getGameComponent";
import {GameModes} from "../../constants/EnumGameModes";
import {WEBSOCKET_URL} from "../../constants/CONST";
import {Client} from "@hapi/nes/lib/client";
import {TokenManager} from "../../services/TokenManager";
import {CantPlayGame} from "./cantPlayGame";

type GameType = {
    gameModeId : number
    difficulty : number
    gameMode : GameModes
}


export const Game: React.FC<GameType> = (props) => {
    const {t} = useTranslation();
    const {isConnected, currentUser, errorToast} = useContext(AppContext)
    const userId = currentUser?.id || null;
    const [text, setText] = useState([""]);

    const [isFirstWord, setIsFirstWord] = useState(true);
    const [currentIndexWord, setCurrentIndexWord] = useState(0); // index of current word in text[]
    const [currentScore, setCurrentScore] = useState(0); // score of current game
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [numberOfWordsPlayed, setNumberOfWordsPlayed] = useState(0); // number of words played in current game
    const [time, setTime] = useState(30); // time left in seconds
    const [lastAccuracy, setLastAccuracy] = useState(1); // accuracy of last game
    const [isFirstGame, setIsFirstGame] = useState(true); // is first game of session
    const [isPopupEndGameOpen, setIsPopupEndGameOpen] = useState(false); // is end game popup open
    const [isPopupInformationsOpen, setIsPopupInformationsOpen] = useState(false); // is informations popup open
    const [canPlay, setCanPlay] = useState(true); // can the user play the game
    const [cantPlayInfo, setCantPlayInfo] = useState<{
        canPlay: boolean,
        reason?: string,
        score?: number,
        date?: string,
        rank?: number
    } | null>(null);
    const [statsNode, setStatsNode] = useState<React.ReactNode>(<></>);

    const wordElement = useRef<HTMLSpanElement>(null); // ref to current word element
    const input = document.querySelector("#game-input") as HTMLInputElement;
    const behindText = useRef<HTMLDivElement>(null);

    let [lastInputPosY, setLastInputPosY] = useState(0)
    const relativeDivRef = useRef<HTMLDivElement>(null);

    const [strategy, setStrategy] = useState(getGameComponent(props.gameMode, finishGame));
    const [clientWS, setClientWS] = useState<Client | null>(null);

    // Check if information popup should be shown automatically on component mount
    useEffect(() => {
        const checkAccess = async () => {
            if (isConnected && strategy.canUserPlay) {
                const accessCheck = await strategy.canUserPlay(t, userId!!, props.gameModeId);

                console.log('access', accessCheck);
                if (!accessCheck.canPlay) {
                    setCanPlay(false);
                    setCantPlayInfo(accessCheck);
                    return;
                }
            }

            if (strategy.shouldShowInformationPopupOnStart()) {
                setIsPopupInformationsOpen(true);
            }
        };

        checkAccess();
        console.log("isConnected? :" + isConnected)
    }, [strategy, isConnected]);


    useEffect(() => {
        // if the user is connected we use the websocket to save the score in db
        // client send every word to the server via websocket
        if (isConnected && clientWS === null) {
            setupWebClient()
        }
    }, []);

    useEffect(() => {
        resetGame()
    }, [clientWS]);

    useEffect(() => {
        // is the user is connected, start a game with websocket
        if (isConnected) {
            resetGame()
        } else { // we just fetch words without websocket
            strategy.fetchWords(props.difficulty, setText);
        }
    }, [props.difficulty]);

    /**
     * Initialize a game with websocket and fetch words from server
     */
    async function wsFetchWordsAndCreateGame() {
        if (clientWS === null) {
            console.error("websocket client is null");
            return;
        }

        let response;
        try {
            response = await clientWS.request({
                method: "POST",
                path: "/ws/game/create",
                payload: {
                    gamemodeId: props.gameModeId,
                    difficultyId: props.difficulty,
                    platformId: 1 // web
                }
            })
        } catch (error) {
            return;
        }

        // get words from response
        setText(response!.payload.words)
    }

    async function wsStartTheGame() {
        if (clientWS === null) {
            console.error("websocket client is null");
            return;
        }

        try {
            await clientWS.request({
                method: "POST",
                path: "/ws/game/start"
            })
        } catch (error) {
            // get the error message from the server
            const errorMessage = (error as any).message || "Unknown error";

            if (errorMessage === "first create a game on /ws/game/create") {
                // try to create a game
                await wsFetchWordsAndCreateGame()
                await clientWS.request({
                    method: "POST",
                    path: "/ws/game/start"
                })
            } else {
                console.error("Error starting the game:", errorMessage);
            }
        }
    }

    /**
     * send the word to the server via websocket
     * @param word
     */
    async function wsSendWord(word: string) {
        if (clientWS === null) {
            console.error("websocket client is null");
            return;
        }

        try {
            await clientWS.request({
                method: "POST",
                path: "/ws/game/wordTyped",
                payload: {
                    word: word,
                }
            })
        } catch (error) {
            errorToast()
            console.error("Error sending the word:", (error as any).message || "Unknown error");
        }
    }

    /**
     * Setup the websocket client and connect to the server
     */
    const setupWebClient = async () => {
        const newClient = new Client(WEBSOCKET_URL);
        await newClient.connect({
            auth: {
                headers: {
                    authorization: `Bearer ${TokenManager.getToken()}`,
                }
            }
        })
        setClientWS(newClient)

        // use to keep the connection alive and detect if the connection is lost
        await newClient.subscribe("/keep-alive", () => {});

        // subscribe to gameEnd event
        // the server send this event when the game is over
        await newClient.subscribe(`/game/gameEnd/${currentUser!.id!}`, async (gameState: any) => {
            setStatsNode(strategy.getStatsFromApiResponse(t, gameState.stats))
            await finishGame();
        })
    }

    /**
     * Launch the function tick every second if the game is started
     */
    useEffect(() => {
        if (isGameStarted) {
            if (isConnected) {
                wsStartTheGame()
            }

            const interval = setInterval(tick, 1000)
            return () => {
                clearInterval(interval)
            }
        } else {
            resetGame()
        }
    }, [isGameStarted])

    /*
     * Function called every second to update the time
     */
    const tick = () => {
        setTime(prevTime => {
            return strategy.tick(prevTime)
        });
    };

    async function finishGame() {
        if (isFirstGame) {
            setIsFirstGame(false)
        }

        const accessCheck = await strategy.canUserPlay(t, userId!!, props.gameModeId);
        if (!accessCheck.canPlay) {
            setCanPlay(false);
            setCantPlayInfo(accessCheck);
            return;
        }

        setIsPopupEndGameOpen(true);
        resetGame();
    }

    const removeFirstLineOfText = () => {
        setText(text.slice(currentIndexWord))
        setCurrentIndexWord(0)
    }

    useEffect(() => {
        resetColor();
    }, [text])

    useEffect(() => {
        if (!isConnected) {
            strategy.onValidWord(currentScore);
        }
    }, [currentScore]);

    useEffect(() => {
        if (isPopupEndGameOpen && input !== null) {
            input.blur();
            input.value = "";
        }
    }, [isPopupEndGameOpen]);

    const resetColor = () => {
        if (!behindText.current) return

        let spans = behindText.current.children;

        for (let i = 0; i < spans.length; i++) {
            let span = spans[i] as HTMLSpanElement;
            span.style.color = "white";
            span.style.opacity = "0.5";
        }
    }

    const updateInput = () => {
        if (!wordElement.current || !input || !relativeDivRef.current) {
            return;
        }

        let rect = wordElement.current.getBoundingClientRect();
        const relativeRect = relativeDivRef.current.getBoundingClientRect();

        // set position
        const newPosY = rect.top - relativeRect.top;

        if (!isFirstWord && newPosY !== lastInputPosY) {
            removeFirstLineOfText();
            rect = wordElement.current.getBoundingClientRect();
        }

        if (isFirstWord) {
            setIsFirstWord(false);
        }

        input.style.left = `${rect.left - relativeRect.left}px`;
        input.style.top = `${rect.top - relativeRect.top}px`;

        setLastInputPosY(newPosY);

        // set width
        const defaultWidth = 400;
        const actualMaxWidth = relativeRect.width - rect.left + relativeRect.left - 20;
        const width = Math.min(defaultWidth, actualMaxWidth);
        input.style.width = `${width}px`;

        // focus and reset
        if (!isPopupEndGameOpen) {
            input.focus();
            input.value = "";
        }
    };

    useEffect(() => {
        updateInput()
    }, [input, currentIndexWord]);

    const resetGame = () => {
        wsFetchWordsAndCreateGame()
        setCurrentScore(prevState => {
            setNumberOfWordsPlayed(prevState1 => {
                if (prevState1 === 0) {
                    setLastAccuracy(0);
                } else {
                    const accuracy = parseFloat((( prevState / prevState1) * 100).toFixed(2));
                    setLastAccuracy(accuracy);
                }
                return 0;
            })
            return 0;
        })
        setCurrentIndexWord(0);
        resetColor();

        setTime(strategy.getTimer());
        setIsGameStarted(false);
        
        if (!isPopupEndGameOpen && input !== null) {
            input.focus();
            input.value = "";
        }
    }

    const OnTextChange = (e: any) => {
        if (!isGameStarted) {
            setIsFirstWord(true)
            setIsGameStarted(true);
        }

        const key = e.key;
        const typedWord = e.target.value.trim();

        if (!text[currentIndexWord].startsWith(typedWord)) {
            input.style.color = "red";
        } else {
            input.style.color = "white";
        }

        if (key !== " ") {
            return;
        }

        // get
        const wordIsCorrect = typedWord === text[currentIndexWord]; // check if typed word is similar to current word in text[]

        // send the word to the server with websocket
        if (isConnected) {
            wsSendWord(typedWord);
        }

        if (wordIsCorrect) {
            setCurrentScore(prevScore => prevScore + 1);
        }
        setNumberOfWordsPlayed(prevState => prevState + 1)

        // ajust color
        wordElement.current!.style.color = wordIsCorrect ? "var(--green)" : "var(--red)";
        wordElement.current!.style.opacity = "1";

        // update
        setCurrentIndexWord(currentIndexWord + 1);
    }

    const showInformationsPopup = ()=> {
        if (isPopupInformationsOpen) {
            setIsPopupInformationsOpen(false);
            return;
        }
        setIsPopupInformationsOpen(true);
    }

    return (
        <div ref={relativeDivRef} className="relative">
            <UiPopup
                className={"p-5 bg-darkblue w-1/5 min-w-fit " + (isConnected ? "h-2/5 min-h-96" : "min-h-fit h-1/5")}
                isOpen={isPopupEndGameOpen}
                onClose={() => {
                    setTimeout(() => {
                        if (input !== null) {
                            input.focus();
                            input.value = "";
                        }
                    }, 1);

                    setIsPopupEndGameOpen(false);
                }}
            >
                <GameResult
                    gameStats={statsNode}
                    closePopup={() => {
                        if (input !== null) {
                            input.focus();
                            input.value = "";
                        }
                        setIsPopupEndGameOpen(false);
                    }}
                />
            </UiPopup>

            <UiPopup
                isOpen={isPopupInformationsOpen}
                onClose={() => {
                    setIsPopupInformationsOpen(false)
                }}
                className={"bg-deep-blue p-6"}
            >
                {strategy.getInformationPopup(t, setIsPopupInformationsOpen)}
            </UiPopup>

            <div className="flex flex-col gap-8">
                {strategy.getTopInfo(t, showInformationsPopup)}

                {(!canPlay || (!isConnected && props.gameMode === GameModes.FASTAILY)) ?
                    <CantPlayGame {...cantPlayInfo}/>
                    :
                    <>
                        <div className="w-full h-85 bg-deep-blue border-2 border-darkpurple-blue p-8 ">
                            <div className="text-justify overflow-hidden h-full"
                                ref={behindText}>
                                {text.map((word, index) => {
                                    if (index === currentIndexWord) {
                                        return (
                                            <span key={index} className="text-3xl" ref={wordElement}>{word} </span>
                                        )
                                    } else {
                                        return (
                                            <span key={index} className="text-3xl">{word} </span>
                                        )
                                    }
                                })}
                            </div>
                        </div>

                        <input
                            id="game-input"
                            className="absolute bg-transparent border-0 outline-none text-white text-3xl"
                            onKeyUp={OnTextChange}
                            autoComplete={"off"}
                        />
                        {strategy.getBottomInfo(currentScore, time, resetGame, t)}
                    </>
                }
            </div>
        </div>
    )
}
